import { Fragment, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { useAngularContext } from 'AngularContext';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { getAoiRecord } from 'Users';
import { getProgramConfigValue } from 'Program';
import { InlineLink } from 'InlineLink';
import { useIsMobile } from '../../settingsShared/useIsMobile';
import { AvatarUpload } from './AvatarUpload';
import { AccountForm } from './AccountForm';
import { ChangePassword } from './ChangePassword';
import { getStudentEmailSectionState, StudentEmailSection } from './StudentEmailSection';
import { DeleteAccountSection } from './DeleteAccountSection';
import { Divider } from '../../settingsShared/Divider';

// Container for the Settings > Account tab (the default Settings section). Reads the converted
// currentUser and lays out each section, replacing the old edit-account + change-password
// directives. On mobile the account form and change-password form toggle (matching the old
// isMobile-driven layout); on desktop both are shown.
export function EditAccount() {
    const { t } = useTranslation('back_royal');
    const $injector = useAngularContext();
    const currentUser = useRequiredCurrentUser();
    const isMobile = useIsMobile();

    const [mobilePasswordOpen, setMobilePasswordOpen] = useState(false);

    // When switching to mobile, always start on the account view (matches the old resize handler).
    useEffect(() => {
        if (isMobile) setMobilePasswordOpen(false);
    }, [isMobile]);

    const userCanHavePassword = !currentUser.hasSamlProvider;
    const programType = getAoiRecord(currentUser)?.programType ?? currentUser.fallbackProgramType;
    const canUploadAvatar =
        !!programType && !!getProgramConfigValue<boolean>(programType, 'supportsAvatarUploadInAccountPage');

    const showProfile = !isMobile || !mobilePasswordOpen;
    const showChangePassword = userCanHavePassword && (!isMobile || mobilePasswordOpen);

    // Shared with StudentEmailSection so the container knows whether to slot it (and its leading
    // divider) into the section list rather than rendering a divider before an empty section.
    const { showStudentId, showStudentEmail } = getStudentEmailSectionState(currentUser);
    const showStudentSection = showStudentId || showStudentEmail;

    const scrollToTop = () => $injector?.get<{ scrollToTop: () => void }>('scrollHelper')?.scrollToTop();

    const openChangePassword = () => {
        setMobilePasswordOpen(true);
        scrollToTop();
    };
    const closeChangePassword = () => {
        setMobilePasswordOpen(false);
        scrollToTop();
    };

    // Visible sections in order; a Divider is rendered between each consecutive pair.
    const sections: { key: string; node: ReactNode }[] = [];
    if (showProfile && canUploadAvatar) sections.push({ key: 'avatar', node: <AvatarUpload /> });
    if (showProfile)
        sections.push({
            key: 'account',
            node: (
                <AccountForm
                    isMobile={isMobile}
                    userCanHavePassword={userCanHavePassword}
                    onOpenChangePassword={openChangePassword}
                />
            ),
        });
    if (showChangePassword)
        sections.push({
            key: 'password',
            node: (
                <>
                    {isMobile ? (
                        // The left arrow mirrors the Change Password button's right arrow (it points in
                        // the reading direction, so it flips for RTL). pt-6 gives the back link the same
                        // breathing room below the header divider that other sections' content gets.
                        <div className="pt-6">
                            <InlineLink onPress={closeChangePassword}>
                                <FontAwesomeIcon icon={faArrowLeft} className="me-2 rtl:rotate-180" />
                                {t('settings.edit_account.back_to_account')}
                            </InlineLink>
                        </div>
                    ) : null}
                    <ChangePassword
                        submitLabel={
                            isMobile
                                ? t('settings.edit_account.save_changes')
                                : t('settings.edit_account.change_password')
                        }
                    />
                </>
            ),
        });
    if (showStudentSection) sections.push({ key: 'student', node: <StudentEmailSection /> });
    sections.push({ key: 'delete', node: <DeleteAccountSection /> });

    // The page header carries `.main-box-header.no-margin` for React sections (see settings.html), so
    // this first section sits flush below the header divider, matching how every other section sits
    // flush below its divider (each section supplies its own vertical padding).
    return (
        <div className="px-4 sm:px-10">
            {sections.map((section, index) => (
                <Fragment key={section.key}>
                    {index > 0 ? <Divider /> : null}
                    {section.node}
                </Fragment>
            ))}
        </div>
    );
}
